import { create } from '@bufbuild/protobuf';
import { Code, ConnectError, createClient } from '@connectrpc/connect';
import {
  CadFileKind,
  CadService,
  CreateSubAssemblyRequestSchema,
  type CreateSubAssemblyRequest,
} from '@puppycad/proto';
import { grpcWebTransport } from '@/services/api/transport/grpc-web-transport';
import type {
  CreateSubAssemblyInput,
  InsertComponentProjectContext,
  InsertComponentService,
  InsertSubAssemblyFromFileInput,
  InsertSubAssemblyNodeType,
  InsertSubAssemblyResult,
} from '../types/insert-component-types';

const cadClient = createClient(CadService, grpcWebTransport);

/**
 * `CreateSubAssemblyRequest` 中由插入来源决定的互斥字段集合。
 */
type CreateSubAssemblySourceFields = Pick<
  CreateSubAssemblyRequest,
  'inputFilePath' | 'inputRepositoryId' | 'inputBranchId' | 'inputCommitId' | 'nodeType'
>;

export interface CreateInsertComponentServiceOptions {
  /** 读取当前插入零部件所需的父装配上下文。 */
  readonly getProjectContext: () => InsertComponentProjectContext | null;
}

/**
 * 从用户选择的显示名和来源信息中解析子装配名称。
 *
 * @param input 统一的子装配创建输入。
 * @returns 可传给 `CreateSubAssemblyRequest.name` 的名称。
 */
function resolveSubAssemblyName(input: CreateSubAssemblyInput): string {
  const displayName = input.displayName.trim();
  if (displayName) {
    return displayName;
  }

  // 文件模式允许旧页面不传显示名，因此继续用来源路径末段兜底以保持兼容。
  if (input.mode !== 'file') {
    return '未命名零部件';
  }

  const pathParts = input.inputFilePath.split('/').filter((part) => part.trim().length > 0);
  return pathParts.at(-1)?.trim() || '未命名零部件';
}

/**
 * 将前端空节点类型映射为后端协议枚举。
 *
 * @param nodeType 前端统一输入中的空节点类型。
 * @returns `CreateSubAssemblyRequest.nodeType` 使用的协议枚举。
 */
function toCreateSubAssemblyNodeType(nodeType: InsertSubAssemblyNodeType): CadFileKind {
  return nodeType === 'part' ? CadFileKind.PART : CadFileKind.ASSEMBLY;
}

/**
 * 将统一输入中的来源模式映射为后端互斥字段。
 *
 * @param input 统一的子装配创建输入。
 * @returns 可合并进 `CreateSubAssemblyRequest` 的来源字段。
 */
function resolveCreateSubAssemblySourceFields(
  input: CreateSubAssemblyInput,
): CreateSubAssemblySourceFields {
  if (input.mode === 'file') {
    const inputFilePath = input.inputFilePath.trim();
    if (!inputFilePath) {
      throw new Error('插入零部件缺少 inputFilePath');
    }

    return {
      inputFilePath,
    };
  }

  if (input.mode === 'repository') {
    if (input.inputRepositoryId <= 0n) {
      throw new Error('创建子装配缺少有效 inputRepositoryId');
    }
    if (input.inputBranchId < 0n) {
      throw new Error('创建子装配 inputBranchId 不能为负数');
    }

    // repository 模式允许 0 分支表示后端默认分支，但仓库 ID 必须明确指向一个来源仓库。
    const inputCommitId = input.inputCommitId?.trim();

    return {
      inputRepositoryId: input.inputRepositoryId,
      inputBranchId: input.inputBranchId,
      ...(inputCommitId ? { inputCommitId } : {}),
    };
  }

  return {
    nodeType: toCreateSubAssemblyNodeType(input.nodeType),
  };
}

/**
 * 校验 CreateSubAssembly 请求是否仍处于有效生命周期。
 *
 * 设计说明：CreateSubAssembly 会直接产生后端装配结构提交；如果页面卸载或写入请求被替换，
 * 应在发起后端结构写入前停止，避免取消后的旧流程继续修改父装配。
 *
 * @param signal 调用方传入的取消信号。
 */
function assertCreateSubAssemblySignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ConnectError('CreateSubAssembly 请求已取消', Code.Canceled);
  }
}

/**
 * 创建插入零部件真实接口服务。
 *
 * @param options 创建选项。
 * @returns 插入零部件服务。
 */
export function createInsertComponentService(
  options: CreateInsertComponentServiceOptions,
): InsertComponentService {
  /**
   * 读取并校验当前父装配上下文。
   *
   * @returns 当前可用于插入零部件的项目上下文。
   */
  function resolveProjectContext(): InsertComponentProjectContext {
    const context = options.getProjectContext();
    if (!context || context.projectId <= 0n || context.parentRepositoryId <= 0n) {
      throw new Error('缺少项目或父装配仓库上下文，无法插入零部件');
    }

    return context;
  }

  /**
   * 基于统一输入创建子装配。
   *
   * @param input 统一的子装配创建输入。
   * @returns 后端创建结果。
   */
  async function createSubAssembly(input: CreateSubAssemblyInput): Promise<InsertSubAssemblyResult> {
    const context = resolveProjectContext();
    const explicitBaseCommitId = input.baseCommitId?.trim() ?? '';
    const contextBaseCommitId = context.currentCommitId?.trim() ?? '';
    // 调用方显式传入提交时，必须以该提交作为并发保护基线。
    const baseCommitId = explicitBaseCommitId || contextBaseCommitId;
    assertCreateSubAssemblySignalActive(input.signal);
    const request = create(CreateSubAssemblyRequestSchema, {
      projectId: context.projectId,
      parentRepositoryId: context.parentRepositoryId,
      ...(context.branchId !== undefined ? { branchId: context.branchId } : {}),
      ...(baseCommitId ? { baseCommitId } : {}),
      name: resolveSubAssemblyName(input),
      ...resolveCreateSubAssemblySourceFields(input),
    });
    const response = await cadClient.createSubAssembly(
      request,
      input.signal ? { signal: input.signal } : undefined,
    );
    assertCreateSubAssemblySignalActive(input.signal);
    if (!response.node) {
      throw new Error('CreateSubAssembly 未返回新建节点');
    }
    const createdCommit = response.createdCommit;
    if (!createdCommit) {
      throw new Error('CreateSubAssembly 未返回 createdCommit');
    }
    // createdCommit 是后续同步主状态的协议凭证，空白 commitId 会让消费方无法定位到可用提交。
    if (!createdCommit.commitId.trim()) {
      throw new Error('CreateSubAssembly 返回的 createdCommit.commitId 不能为空');
    }

    return {
      node: response.node,
      createdCommit,
    };
  }

  /**
   * 基于用户目录 CAD 文件创建子装配。
   *
   * @param input 用户选中的 CAD 文件来源。
   * @returns 后端创建结果。
   */
  async function createSubAssemblyFromFile(
    input: InsertSubAssemblyFromFileInput,
  ): Promise<InsertSubAssemblyResult> {
    return createSubAssembly({
      mode: 'file',
      inputFilePath: input.inputFilePath,
      displayName: input.displayName,
      signal: input.signal,
    });
  }

  return {
    createSubAssembly,
    createSubAssemblyFromFile,
  };
}
